import SvgArrowLeft from '@petplate/icons/core/ArrowLeft';
import Close from '@petplate/icons/core/Close';
import Button from '@petplate/ui/components/Button';
import SubTheme from '@refract-ui/sc/components/SubTheme';
import lens from '@refract-ui/sc/lens';
import React from 'react';
import ReactModal from 'react-modal';
import { createGlobalStyle, css } from 'styled-components';
import { useCurrentTheme } from '../ThemeProvider/ThemeProvider';

const ModalGlobalStyle = createGlobalStyle`
  .ReactModal__Body--open {
    overflow: hidden;
  }

  .ReactModal__Overlay {
    align-items: center;
    justify-content: flex-end;
    opacity: 0;
    transition: opacity .3s ease-in-out;
  }

  .ReactModal__Overlay--after-open {
    opacity: 1;

    ${({ theme: { mq } }) => mq.lg`
      justify-content: center;
    `}
  }

  .ReactModal__Overlay--before-close {
    opacity: 0;
  }

  .ReactModal__Content--before-close {
    height: 0;
  }

  .ReactModal__Content--after-open {
    height: auto;
  }

  .ReactModal__Content {
    border-radius: 0.75rem 0.75rem 0 0;
    margin: 3.75rem auto 0;
    overflow: auto;
    padding: 0;
    position: relative;
    width: 100%;
    outline: none;
    transition: height .3s ease-in-out;

    ${({ theme: { mq } }) => mq.lg`
      border-radius: 0.75rem;
      margin: 0 auto;
      width: auto;
    `}
  }
`;

const ContentWrapper = lens.div()<{ $large: Modal['large'] }>`
  ${({ theme: { box, allColors, mq, fontStacks }, $large }) => css`
    background: ${allColors.pureWhite};
    border-radius: 0.75rem 0.75rem 0 0;
    margin: 0 auto;
    display: grid;
    grid-template-areas:
      'header'
      'content';
    grid-template-columns: 1fr;
    grid-template-rows: max-content 1fr;
    min-width: 100vw;
    position: relative;
    z-index: 1001;
    ${box.t('bodyMd')}

    h1:nth-child(1) {
      ${box.t('h3')};
      margin-top: 0;
    }

    p {
      ${box.t('bodyMd')}
    }

    ${mq.md`
      min-width: 100vw;
    `}

    ${mq.lg`
      border-radius: 0.75rem;
      min-width: 23.4375rem;
      max-width: 23.4375rem;

      ${
        $large &&
        `
          min-width: 30rem;
          max-width: 53rem;
        `
      }
    `}
  `}
`;

const Header = lens.div()`
  ${({ theme: { allColors } }) => css`
    display: flex;
    align-items: center;
    background: ${allColors.pureWhite};
    position: sticky;
    padding: 0.75rem 1.5rem 0.25rem;
    top: 0;
    z-index: 100;
    flex: 1;
  `}
`;

const Content = lens.div()<{ $full: Modal['full'] }>`
  ${({ $full }) => css`
    padding: ${$full ? '0' : '0 1.5rem 1.5rem'};
  `}
`;

const BackButton = lens()(Button)`
  ${({ theme: { allColors } }) => css`
    & > div {
      justify-content: start;
    }

    svg path {
      fill: ${allColors.secondary};
    }
  `}
`;

const CloseButton = lens()(Button)`
  ${({ theme: { allColors } }) => css`
    margin-left: auto;
    margin-right: -0.3rem;

    & > div {
      justify-content: end;
    }

    svg path {
      fill: ${allColors.fg};
    }
  `}
`;

const settings = {
  style: {
    overlay: {
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column' as const,
      zIndex: 1000
    }
  }
};

interface Modal {
  show: boolean;
  onBack?: () => void;
  close?: (event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => void;
  className?: string;
  children: React.ReactNode;
  large?: boolean;
  full?: boolean;
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  showHeader?: boolean;
}

const Modal: React.FC<Modal> = ({
  show,
  onBack,
  close,
  className,
  children,
  large,
  full,
  containerRef,
  showHeader = true
}) => {
  const theme = useCurrentTheme();

  return (
    show && (
      <>
        <ModalGlobalStyle />
        <ReactModal
          className={`petplate-modal ${className}`}
          isOpen={show}
          onRequestClose={close}
          ariaHideApp={false}
          contentRef={containerRef ? (ref) => (containerRef.current = ref) : undefined}
          {...settings}
        >
          <SubTheme theme={theme}>
            <ContentWrapper $large={large}>
              {showHeader && (
                <Header>
                  {onBack && (
                    <BackButton
                      variant="text"
                      label="Back"
                      onClick={onBack}
                      size="small"
                      leadingIcon={SvgArrowLeft}
                    />
                  )}
                  <CloseButton
                    label=""
                    variant="text"
                    size="small"
                    onClick={close}
                    trailingIcon={Close}
                    aria-label="close modal"
                  />
                </Header>
              )}
              <Content $full={full}>{children}</Content>
            </ContentWrapper>
          </SubTheme>
        </ReactModal>
      </>
    )
  );
};

export default Modal;
